import React from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocationSearchableField from "../../LocationSearchableField";

const PURPLE = "#7C3AED";
const LABEL = "#344054";
const BORDER = "#D0D5DD";
const ERROR = "#D92D20";

function getCandidateNamePart(candidate, part) {
  const fullName = String(candidate?.full_name || "").trim();
  const nameParts = fullName ? fullName.split(/\s+/) : [];

  return part === "first" ? nameParts[0] || "" : nameParts.slice(1).join(" ");
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 44,
    borderRadius: 2,
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: BORDER,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#BFC8D4",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: PURPLE,
      borderWidth: 1.5,
    },
  },
  "& .MuiInputBase-input": {
    fontSize: 14,
    color: "#344054",
    py: 1.35,
    "&::placeholder": {
      color: "#98A2B3",
      opacity: 1,
    },
  },
  "& .MuiFormHelperText-root": {
    mx: 0,
    mt: 0.6,
  },
};

function LabelText({ children }) {
  return (
    <Typography data-popup-label
      sx={{
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1,
        color: LABEL,
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

function Section({ children }) {
  return (
    <Typography data-popup-section
      sx={{
        fontSize: 16,
        fontWeight: 700,
        color: "#3451F4",
        lineHeight: "24px",
      }}
    >
      {children}
    </Typography>
  );
}

function OverviewField({
  value,
  onChange,
  placeholder,
  error,
  helperText,
  showArrow = false,
  select = false,
  options = [],
}) {
  if (select) {
    return (
      <TextField
        select
        value={value || ""}
        onChange={onChange}
        size="small"
        fullWidth
        error={!!error}
        helperText={helperText || ""}
        sx={fieldSx}
      >
        {options.map((option) => (
          <MenuItem
            key={String(option?.value || option?.label || "")}
            value={option?.value || ""}
          >
            {option?.label || option?.value || ""}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  return (
    <TextField
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      size="small"
      fullWidth
      error={!!error}
      helperText={helperText || ""}
      sx={fieldSx}
      InputProps={
        showArrow
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  <ExpandMoreRoundedIcon
                    sx={{ fontSize: 20, color: "#667085" }}
                  />
                </InputAdornment>
              ),
            }
          : undefined
      }
    />
  );
}

export default function StartFromScratchCandidateStep({
  firstName,
  lastName,
  form,
  errors,
  candidateLookupValue,
  candidateLookupInputValue,
  candidateLookupOptions = [],
  candidateLookupLoading = false,
  candidateLookupHelperText = "",
  onFirstChange,
  onLastChange,
  onCandidateLookupChange,
  onCandidateLookupInputChange,
  onFieldChange,
  existingCandidateOnly = false,
  showCandidateLookupField = true,
  showJobField = false,
  jobOptions = [],
  jobsLoading = false,
  showStageField = false,
  stageOptions = [],
  statusOptions = [],
  preferredJobTypeOptions = [],
  availabilityOptions = [],
}) {
  const nameSearchOptions = candidateLookupOptions.filter((option) => !option?.createOption);
  const renderNameSearch = (value, onInputChange, placeholder, showIcon = false) => {
    const namePart = showIcon ? "first" : "last";

    return (
    <Autocomplete
      freeSolo
      sx={{ flex: 1, minWidth: 0 }}
      options={nameSearchOptions}
      filterOptions={(options) => options}
      loading={candidateLookupLoading}
      inputValue={value || ""}
      onInputChange={(_, nextValue, reason) => {
        if (reason !== "reset") onInputChange({ target: { value: nextValue || "" } });
      }}
      onChange={(_, option) => {
        if (option && typeof option !== "string") onCandidateLookupChange(_, option);
      }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : getCandidateNamePart(option, namePart)
      }
      noOptionsText={candidateLookupLoading ? "Searching candidates..." : "No candidates found — continue to add a new candidate."}
      slotProps={{ popper: { placement: "bottom-start", modifiers: [{ name: "flip", enabled: false }] } }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option?.id || option?.full_name || option?.email || ""}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {getCandidateNamePart(option, namePart) || "Candidate"}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "#667085" }} noWrap>
              {[option?.title, option?.email].filter(Boolean).join(" • ") || option?.location || ""}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          fullWidth
          placeholder={placeholder}
          autoComplete="new-password"
          inputProps={{
            ...params.inputProps,
            name: showIcon ? "candidate-first-name-search" : "candidate-last-name-search",
            autoComplete: "new-password",
          }}
          sx={fieldSx}
          InputProps={{
            ...params.InputProps,
            startAdornment: showIcon ? <><InputAdornment position="start"><PersonOutlineIcon sx={{ fontSize: 18, color: PURPLE }} /></InputAdornment>{params.InputProps.startAdornment}</> : params.InputProps.startAdornment,
            endAdornment: <>{candidateLookupLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>,
          }}
        />
      )}
    />
    );
  };

  return (
    <Box sx={{ px: 4, pt: 4, pb: 4 }}>
      <Section>
        {existingCandidateOnly ? "Add Existing Candidate" : "Candidate Overview"}
      </Section>

      <Box sx={{ mt: 2.25 }}>
        <Stack spacing={2}>
          {errors?._form ? <Alert severity="error">{errors._form}</Alert> : null}

          {showCandidateLookupField ? (
            <Box>
              <LabelText>Search Existing Candidate (Optional)</LabelText>
              <Autocomplete
                options={candidateLookupOptions}
                loading={candidateLookupLoading}
                value={candidateLookupValue}
                inputValue={candidateLookupInputValue}
                onChange={onCandidateLookupChange}
                onInputChange={onCandidateLookupInputChange}
                openOnFocus
                clearOnBlur={false}
                filterOptions={(options) => options}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  if (option?.createOption) return option.full_name || option.inputValue || "";
                  return String(option?.full_name || "");
                }}
                isOptionEqualToValue={(option, value) =>
                  String(option?.id || option?.inputValue || "") ===
                  String(value?.id || value?.inputValue || "")
                }
                renderOption={(props, option) => {
                  if (option?.createOption) {
                    return (
                      <Box component="li" {...props} key={`create-${option.inputValue || option.full_name || ""}`}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              bgcolor: "#F7F5FF",
                              color: PURPLE,
                              border: `1px solid ${BORDER}`,
                            }}
                          >
                            <PersonOutlineIcon sx={{ fontSize: 16 }} />
                          </Avatar>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                              Create new candidate
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: "#667085" }} noWrap>
                              {option.full_name || option.inputValue || ""}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  }

                  return (
                    <Box component="li" {...props} key={option?.id || option?.full_name || option?.email || ""}>
                      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: "100%" }}>
                        <Avatar
                          src={option?.avatar_url || ""}
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "#F7F5FF",
                            color: PURPLE,
                            border: `1px solid ${BORDER}`,
                          }}
                        >
                          {(option?.full_name || "?").charAt(0).toUpperCase()}
                        </Avatar>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }} noWrap>
                            {option?.full_name || "Candidate"}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: "#667085" }} noWrap>
                            {[option?.title, option?.email].filter(Boolean).join(" • ") || option?.location || "-"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search by first name or last name"
                    helperText={candidateLookupHelperText}
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Avatar
                            sx={{
                              width: 22,
                              height: 22,
                              bgcolor: "#F7F5FF",
                              color: PURPLE,
                              border: `1px solid ${BORDER}`,
                            }}
                          >
                            <PersonOutlineIcon sx={{ fontSize: 13 }} />
                          </Avatar>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {candidateLookupLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          ) : null}

          {!existingCandidateOnly ? (
            <>
              <Box>
                <LabelText>Name</LabelText>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  {renderNameSearch(firstName, onFirstChange, "First Name", true)}
                  {renderNameSearch(lastName, onLastChange, "Last Name")}
                </Stack>
                {errors?.full_name ? (
                  <Typography sx={{ mt: 0.7, fontSize: 11.5, color: ERROR }}>
                    {errors.full_name}
                  </Typography>
                ) : null}
              </Box>

              <Box>
                <LabelText>Email</LabelText>
                <OverviewField
                  value={form?.email}
                  onChange={onFieldChange("email")}
                  placeholder="stan.lee@domain.com"
                  showArrow={true}
                  error={!!errors?.email}
                  helperText={errors?.email || ""}
                />
              </Box>

              <Box>
                <LabelText>Phone</LabelText>
                <OverviewField
                  value={form?.phone}
                  onChange={onFieldChange("phone")}
                  placeholder="+351 919 880 032"
                  showArrow={true}
                  error={!!errors?.phone}
                  helperText={errors?.phone || ""}
                />
              </Box>

              <Box>
                <LabelText>Location (city / country)</LabelText>
                <LocationSearchableField
                  value={form?.location}
                  onChange={(nextValue) =>
                    onFieldChange("location")({ target: { value: nextValue } })
                  }
                  placeholder="New York, USA"
                  textFieldProps={{
                    error: !!errors?.location,
                    helperText: errors?.location || "",
                    sx: fieldSx,
                  }}
                />
              </Box>

              <Box>
                <LabelText>Primary Role / Expertise</LabelText>
                <OverviewField
                  value={form?.title}
                  onChange={onFieldChange("title")}
                  placeholder="Full-Stack Engineer"
                  showArrow={true}
                  error={!!errors?.title}
                  helperText={errors?.title || ""}
                />
              </Box>

              <Box>
                <LabelText>Status</LabelText>
                <OverviewField
                  select
                  value={form?.status}
                  onChange={onFieldChange("status")}
                  options={statusOptions}
                  error={!!errors?.status}
                  helperText={errors?.status || ""}
                />
              </Box>

              {showJobField ? (
                <Box>
                  <LabelText>Job (optional)</LabelText>
                  <Autocomplete
                    options={jobOptions}
                    loading={jobsLoading}
                    value={
                      jobOptions.find(
                        (option) => String(option?.value) === String(form?.job_id || "")
                      ) || null
                    }
                    onChange={(_event, option) =>
                      onFieldChange("job_id")({ target: { value: option?.value || "" } })
                    }
                    getOptionLabel={(option) => String(option?.label || "")}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.value || "") === String(value?.value || "")
                    }
                    noOptionsText={jobsLoading ? "Loading jobs..." : "No matching jobs"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Search jobs..."
                        error={!!errors?.job_id}
                        helperText={errors?.job_id || ""}
                        sx={fieldSx}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {jobsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              ) : null}

              <Box>
                <LabelText>Preferred Job Types (optional)</LabelText>
                <Autocomplete
                  multiple
                  options={preferredJobTypeOptions}
                  value={String(form?.job_types || "")
                    .split(",")
                    .map((value) => value.trim())
                    .filter(Boolean)}
                  onChange={(_event, nextValues) =>
                    onFieldChange("job_types")({ target: { value: nextValues.join(", ") } })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select job types"
                      sx={fieldSx}
                    />
                  )}
                />
              </Box>

              <Box>
                <LabelText>Availability (optional)</LabelText>
                <OverviewField
                  select
                  value={form?.availability}
                  onChange={onFieldChange("availability")}
                  options={[{ value: "", label: "Optional" }, ...availabilityOptions.map((value) => ({ value, label: value }))]}
                />
              </Box>

              {showStageField ? (
                <Box>
                  <LabelText>Candidate Stage</LabelText>
                  <OverviewField
                    select
                    value={form?.stage}
                    onChange={onFieldChange("stage")}
                    options={stageOptions}
                    error={!!errors?.stage}
                    helperText={errors?.stage || ""}
                  />
                </Box>
              ) : null}
            </>
          ) : null}

          {existingCandidateOnly && showStageField ? (
            <Box>
              <LabelText>Stage</LabelText>
              <OverviewField
                select
                value={form?.stage}
                onChange={onFieldChange("stage")}
                options={stageOptions}
                error={!!errors?.stage}
                helperText={errors?.stage || ""}
              />
            </Box>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}
